import { fontFamilies } from '@cfra-nextgen-frontend/shared/src/utils/fonts';
import { SxProps, Theme } from '@mui/material';

// ─── Shared constants ────────────────────────────────────────────────────────

export const SCROLL_THRESHOLD_PX = 80;

/** Visually hidden but readable by screen readers */
export const srOnly: React.CSSProperties = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
};

// ─── Colour helpers ───────────────────────────────────────────────────────────

export function getSecurityTypeColor(type: string): { bg: string; text: string } {
    switch (type.toLowerCase()) {
        case 'etf':
            return { bg: '#E3F2FD', text: '#1565C0' };
        case 'fund':
            return { bg: '#F3E5F5', text: '#6A1B9A' };
        case 'stock':
        default:
            return { bg: '#E8F5E9', text: '#2E7D32' };
    }
}

// ─── Page layout ─────────────────────────────────────────────────────────────

/** Outer column wrapper: page content + footer fill the viewport below the
 * 52px top nav. The page grows to push the footer to the bottom when content
 * is short, and the whole thing scrolls naturally when content is tall. */
export const pageWithFooterSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'calc(100vh - 52px)',
};

export const pageContainerSx: SxProps<Theme> = {
    flex: 1,
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    backgroundColor: '#f2f5fc',
};

export const sidebarSx: SxProps<Theme> = {
    width: { xs: '100%', md: '350px' },
    flexShrink: 0,
    backgroundColor: '#fff',
    borderRight: { md: '1px solid #e0e0e0' },
    borderBottom: { xs: '1px solid #e0e0e0', md: 'none' },
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
};

export const mainContentSx: SxProps<Theme> = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f2f5fc',
    p: 3,
};

// ─── Search input ─────────────────────────────────────────────────────────────

export const searchInputSx: SxProps<Theme> = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '4px',
        backgroundColor: '#F3F5FC',
        '& fieldset': { borderColor: '#E4E4E4' },
        '&:hover fieldset': { borderColor: '#007AB9' },
        '&.Mui-focused fieldset': { borderColor: '#007AB9' },
    },
    '& .MuiInputBase-input': {
        fontFamily: fontFamilies.GraphikRegular,
        fontSize: '13px',
        color: '#222',
        '&::placeholder': { color: '#aaa', opacity: 1 },
    },
};

export const dropdownContainerSx: SxProps<Theme> = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 200,
    border: '1px solid #E4E4E4',
    borderRadius: '4px',
    backgroundColor: '#fff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    maxHeight: '260px',
    overflowY: 'auto',
    '&::-webkit-scrollbar': { width: '4px' },
    '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: '4px' },
};

// ─── Search result row ────────────────────────────────────────────────────────

export const resultRowSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 1.5,
    py: 0.75,
    gap: 1,
    cursor: 'pointer',
    borderBottom: '1px solid #F3F5FC',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { backgroundColor: '#F3F5FC' },
    '&:focus': {
        backgroundColor: '#F3F5FC',
        outline: '2px solid #007AB9',
        outlineOffset: '-2px',
    },
};

export const resultRowChipSx: SxProps<Theme> = {
    flexShrink: 0,
    fontSize: '10px',
    height: '18px',
    fontFamily: fontFamilies.GraphikRegular,
    '& .MuiChip-label': { px: 0.75 },
};

// ─── Empty state ──────────────────────────────────────────────────────────────

export const emptyStateSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 1,
};

// ─── Security details card ────────────────────────────────────────────────────

export const detailsCardSx: SxProps<Theme> = {
    width: '100%',
    maxWidth: '560px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    p: 3,
};

export const detailsCardHeaderSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    mb: 2.5,
};

export const detailsCardChipSx: SxProps<Theme> = {
    fontFamily: fontFamilies.GraphikMedium,
    fontSize: '11px',
    '& .MuiChip-label': { px: 1 },
};

export const detailsRowSx: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'space-between',
    py: 1,
    borderBottom: '1px solid #f0f0f0',
    '&:last-child': { borderBottom: 'none' },
};

// ─── Chart type section ───────────────────────────────────────────────────────

/** Wrapper section for the chart type control — adds top spacing/divider */
export const chartTypeSectionSx: SxProps<Theme> = {
    mt: 2,
    pt: 2,
    borderTop: '1px solid #F0F0F0',
};

/** Styles the Select control to match the existing search input aesthetic */
export const chartTypeSelectSx: SxProps<Theme> = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '4px',
        backgroundColor: '#F3F5FC',
        '& fieldset': { borderColor: '#E4E4E4' },
        '&:hover fieldset': { borderColor: '#007AB9' },
        '&.Mui-focused fieldset': { borderColor: '#007AB9' },
    },
    '& .MuiInputBase-input': {
        fontFamily: fontFamilies.GraphikRegular,
        fontSize: '13px',
        color: '#222',
    },
    '&.Mui-disabled .MuiInputBase-input': { color: '#aaa' },
};

/** Load Chart button — full-width, primary action */
export const loadChartButtonSx: SxProps<Theme> = {
    mt: 2,
    fontFamily: fontFamilies.GraphikMedium,
    fontSize: '13px',
    textTransform: 'none',
    borderRadius: '4px',
    backgroundColor: '#007AB9',
    '&:hover': { backgroundColor: '#005f8e' },
    '&.Mui-disabled': {
        backgroundColor: '#E4E4E4',
        color: '#aaa',
    },
};

// ─── Chart data section (shared shell across all chart types) ────────────────

/** Outer card that wraps header + summary banner + chart-type-specific content */
export const chartDataSectionSx: SxProps<Theme> = {
    width: '100%',
    backgroundColor: '#fff',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    overflow: 'hidden',
    alignSelf: 'flex-start',
};

/** Dark navy header band — same for every chart type */
export const chartDataSectionHeaderSx: SxProps<Theme> = {
    backgroundColor: '#0A2540',
    color: '#fff',
    px: 3,
    py: 2,
};

/** Light blue summary banner — same position for every chart type, text varies */
export const chartDataSectionSummaryBannerSx: SxProps<Theme> = {
    mx: 3,
    mt: 2,
    px: 2,
    py: 1.5,
    backgroundColor: '#E8F2FB',
    borderRadius: '4px',
    fontFamily: fontFamilies.GraphikRegular,
    fontSize: '13px',
    color: '#002B5A',
    lineHeight: 1.5,
};

/** Slot below the summary banner where each chart type's controls render */
export const chartDataSectionContentSx: SxProps<Theme> = {
    px: 3,
    py: 2.5,
    borderTop: '1px solid #f0f0f0',
    mt: 2,
};

// ─── Perf 10k chart-type specifics ───────────────────────────────────────────

/** Three-column grid that collapses to stacked on narrow viewports */
export const perf10kDropdownsGridSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
    gap: 2,
    mt: 1.5,
};

/** Mirrors chartTypeSelectSx so the three native MUI Selects feel identical */
export const perf10kSelectSx: SxProps<Theme> = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '4px',
        backgroundColor: '#F3F5FC',
        '& fieldset': { borderColor: '#E4E4E4' },
        '&:hover fieldset': { borderColor: '#007AB9' },
        '&.Mui-focused fieldset': { borderColor: '#007AB9' },
    },
    '& .MuiInputBase-input': {
        fontFamily: fontFamilies.GraphikRegular,
        fontSize: '13px',
        color: '#222',
    },
    '&.Mui-disabled .MuiInputBase-input': { color: '#aaa' },
};

/** Right-pane wrapper that switches the main-content alignment to top-anchored
 * (top center) once a security is selected — used for both the Selected Security
 * card and the wide chart panel, where the default centered layout looks off. */
export const mainContentPanelSx: SxProps<Theme> = {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    backgroundColor: '#f2f5fc',
    p: 3,
};
